/**
 * XML History Tab — Eşleştirme Geçmişi (kart tabanlı, expandable detay).
 *
 * Her kart: tarih/saat + süre + status pill + özet grid (yeni/güncellenen/
 * pasife alınan/fotoğrafı eksik). "Detayı Göster" → full metrikler + hata
 * log'u + snapshot varsa "Geri Al" (rollback) butonu.
 *
 * Snapshot ancak destructive op'larda (URL swap / SKU migration / admin
 * reset) alınır — sıradan sync'te snapshot_id null olur, "Geri Al" butonu
 * gösterilmez. Bu tamamen bilinçli: sıradan sync zaten geri alınabilir
 * çünkü ürünler soft-delete + canonical_sku ile korunur.
 */
import { useCallback, useEffect, useState } from "react";
import {
    History, Loader2, CheckCircle2, AlertTriangle, X, Clock, ChevronDown,
    ChevronUp, RefreshCcw, RotateCcw, PlusCircle, Archive, Image as ImageIcon,
    Package, Filter, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_META = {
    queued:      { pill: "bg-info-soft text-info border-info/30",                    label: "Kuyrukta",       Icon: Loader2,        spinning: true  },
    downloading: { pill: "bg-info-soft text-info border-info/30",                    label: "İndiriliyor",    Icon: Loader2,        spinning: true  },
    parsing:     { pill: "bg-info-soft text-info border-info/30",                    label: "Ayrıştırılıyor", Icon: Loader2,        spinning: true  },
    writing:     { pill: "bg-warning-soft text-warning border-warning/30",           label: "Yazılıyor",      Icon: Loader2,        spinning: true  },
    success:     { pill: "bg-success-soft text-success border-success/30",           label: "Başarılı",       Icon: CheckCircle2,   spinning: false },
    failed:      { pill: "bg-destructive/12 text-destructive border-destructive/30", label: "Başarısız",      Icon: AlertTriangle,  spinning: false },
    cancelled:   { pill: "bg-muted text-muted-foreground border-border",             label: "İptal",          Icon: X,              spinning: false },
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
};
const fmtDuration = (start, end) => {
    if (!start || !end) return "—";
    try {
        const ms = new Date(end) - new Date(start);
        if (ms < 1000) return `${ms} ms`;
        if (ms < 60_000) return `${(ms / 1000).toFixed(1)} sn`;
        const mins = Math.floor(ms / 60_000);
        const secs = Math.floor((ms % 60_000) / 1000);
        return `${mins} dk ${secs} sn`;
    } catch { return "—"; }
};
const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString("tr-TR") : "—");

export default function XmlHistoryTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(new Set());
    const [rollback, setRollback] = useState(null);  // {jobId, snapshotId}
    const [rollbackBusy, setRollbackBusy] = useState(false);

    const load = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);
            const { data } = await api.get("/xml/feed/jobs");
            setItems(data.items || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(() => load({ silent: true }), 15_000);
        return () => clearInterval(t);
    }, [load]);

    const toggle = (id) => {
        setExpanded((cur) => {
            const c = new Set(cur);
            if (c.has(id)) c.delete(id);
            else c.add(id);
            return c;
        });
    };

    const doRollback = async () => {
        if (!rollback?.snapshotId) return;
        try {
            setRollbackBusy(true);
            const { data } = await api.post(`/xml/feed/sync/rollback/${rollback.snapshotId}`);
            toast.success(`Geri alma başarılı — ${data.restored} ürünün ataması eski haline döndürüldü.`);
            setRollback(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Geri alma başarısız.");
        } finally {
            setRollbackBusy(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="xml-history-tab">
            <div className="rounded-xl border border-border bg-surface">
                <div className="px-5 pt-5 pb-3 border-b border-border flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-muted grid place-items-center shrink-0 border border-border">
                        <History className="h-4 w-4" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-display text-base font-semibold tracking-tight">Eşleştirme Geçmişi</h3>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                            Son 20 eşleştirme. Kayıtlar ne kadar sürdüğünü, neyin değiştiğini gösterir. Kritik bir işlem sonrası
                            (URL değişimi, ürün kodu güncellemesi, tam sıfırlama) otomatik yedek alındıysa <em>Detayı Göster</em>
                            içinde &quot;Bu noktaya geri al&quot; butonu görünür.
                        </p>
                    </div>
                    <button
                        onClick={() => load()}
                        className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-muted transition-colors shrink-0"
                        data-testid="xml-history-refresh"
                    >
                        <RefreshCcw className="h-3 w-3" /> Yenile
                    </button>
                </div>

                <div className="p-5 space-y-2.5">
                    {loading ? (
                        <div className="py-8 text-center text-[11.5px] text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Yükleniyor…
                        </div>
                    ) : items.length === 0 ? (
                        <EmptyState />
                    ) : (
                        items.map((j) => (
                            <HistoryRow
                                key={j.job_id}
                                job={j}
                                expanded={expanded.has(j.job_id)}
                                onToggle={() => toggle(j.job_id)}
                                onRollback={() => setRollback({ jobId: j.job_id, snapshotId: j.snapshot_id })}
                            />
                        ))
                    )}
                </div>
            </div>

            <AlertDialog open={!!rollback} onOpenChange={(v) => !v && !rollbackBusy && setRollback(null)}>
                <AlertDialogContent data-testid="xml-rollback-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <RotateCcw className="h-4 w-4" /> Bu noktaya geri alalım mı?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2 text-xs leading-relaxed">
                                <p>
                                    Bu eşleştirmeden ÖNCEKİ &quot;tablo atamaları, kalıp, fit notu, model, sizingImage, model bedeni&quot; değerleri
                                    mevcut ürünlere geri yazılır. XML kaynaklı alanlar (isim/fotoğraf/kategori) etkilenmez.
                                </p>
                                <div className="rounded-md border border-info/30 bg-info-soft/40 px-2.5 py-1.5 flex items-start gap-1.5">
                                    <ShieldCheck className="h-3 w-3 text-info shrink-0 mt-0.5" />
                                    <span className="text-[11px] text-foreground/85">
                                        Bu işlem güvenlidir — canlı ürün kayıtlarında bir sonraki değişiklikte yeni bir yedek de alınır.
                                    </span>
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={rollbackBusy} data-testid="xml-rollback-cancel">Vazgeç</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={rollbackBusy}
                            onClick={(e) => { e.preventDefault(); doRollback(); }}
                            data-testid="xml-rollback-confirm"
                        >
                            {rollbackBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
                            Geri Al
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

const EmptyState = () => (
    <div className="rounded-lg border border-dashed border-border p-8 text-center" data-testid="xml-history-empty">
        <History className="h-6 w-6 mx-auto text-muted-foreground mb-2" strokeWidth={1.5} />
        <div className="text-[13px] font-medium">Henüz eşleştirme yapılmamış</div>
        <p className="text-[11px] text-muted-foreground mt-1 max-w-sm mx-auto leading-relaxed">
            İlk eşleştirmeni başlattığında burası dolar. Manuel eşleştirme Kurulum sekmesinden, otomatik eşleştirme
            zamanlaması Sayz destek ekibi tarafından yapılır.
        </p>
    </div>
);

/* ============================================================================
   History satırı (kart)
============================================================================ */
const HistoryRow = ({ job, expanded, onToggle, onRollback }) => {
    const meta = STATUS_META[job.status] || STATUS_META.queued;
    const StatusIcon = meta.Icon;
    const duration = fmtDuration(job.started_at, job.finished_at);
    const hasRollback = Boolean(job.snapshot_id);
    const isRunning = ["queued", "downloading", "parsing", "writing"].includes(job.status);

    return (
        <div className={`rounded-lg border transition-colors ${expanded ? "border-foreground/25" : "border-border"} bg-background`} data-testid={`xml-history-row-${job.job_id}`}>
            {/* Kart üst satır — özet */}
            <button
                onClick={onToggle}
                className="w-full p-3 text-left flex items-center gap-3 flex-wrap hover:bg-muted/30 transition-colors rounded-lg"
                data-testid={`xml-history-toggle-${job.job_id}`}
            >
                {/* Sol: durum ikonu */}
                <div className="h-8 w-8 rounded-md bg-muted grid place-items-center shrink-0 border border-border">
                    <StatusIcon
                        className={`h-3.5 w-3.5 ${meta.pill.split(" ").filter((s) => s.startsWith("text-"))[0] || ""} ${meta.spinning ? "animate-spin" : ""}`}
                        strokeWidth={2}
                    />
                </div>

                {/* Orta: tarih + süre + status */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13px] font-semibold font-mono tabular-nums" data-testid={`xml-history-date-${job.job_id}`}>
                            {fmtDate(job.started_at)}
                        </span>
                        <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${meta.pill}`}>{meta.label}</Badge>
                        {hasRollback && (
                            <Badge variant="outline" className="text-[9.5px] uppercase tracking-wider bg-info-soft text-info border-info/30">
                                <RotateCcw className="h-2.5 w-2.5 mr-1" /> Yedek alındı
                            </Badge>
                        )}
                    </div>
                    <div className="text-[10.5px] text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {duration}</span>
                        {job.total_seen > 0 && <span>· {fmtNum(job.total_seen)} ürün görüldü</span>}
                    </div>
                </div>

                {/* Sağ: özet metrikler + expand ikonu */}
                <div className="flex items-center gap-2 shrink-0">
                    {!isRunning && (
                        <div className="hidden sm:flex items-center gap-1.5">
                            {job.total_new > 0    && <QuickMetric icon={PlusCircle} value={job.total_new}    tone="success" />}
                            {job.total_updated > 0 && <QuickMetric icon={RefreshCcw} value={job.total_updated} tone="info"    />}
                            {job.total_archived > 0 && <QuickMetric icon={Archive}    value={job.total_archived} tone="warning" />}
                        </div>
                    )}
                    {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </div>
            </button>

            {/* Detay paneli */}
            {expanded && (
                <div className="p-4 border-t border-border space-y-3" data-testid={`xml-history-detail-${job.job_id}`}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                        <DetailBox label="Yeni Eklenen"    value={job.total_new}          icon={PlusCircle} tone="success" testId={`xml-history-new-${job.job_id}`} />
                        <DetailBox label="Güncellenen"     value={job.total_updated}      icon={RefreshCcw} tone="info"    testId={`xml-history-updated-${job.job_id}`} />
                        <DetailBox label="Değişmeyen"      value={job.total_unchanged}    icon={Filter}     tone="muted"   testId={`xml-history-unchanged-${job.job_id}`} />
                        <DetailBox label="Pasife Alınan"   value={job.total_archived}     icon={Archive}    tone="warning" testId={`xml-history-archived-${job.job_id}`} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                        <DetailBox label="Toplam Görülen"  value={job.total_seen}         icon={Package}    tone="muted"   testId={`xml-history-seen-${job.job_id}`} />
                        <DetailBox label="Atlandı"          value={job.total_skipped}      icon={X}          tone="muted"   testId={`xml-history-skipped-${job.job_id}`} />
                        <DetailBox label="Fotoğrafı Eksik" value={job.total_broken_images} icon={ImageIcon}  tone="muted"   testId={`xml-history-broken-${job.job_id}`} />
                        <DetailBox label="Süre"             value={duration}               icon={Clock}      tone="muted"   isText testId={`xml-history-duration-${job.job_id}`} />
                    </div>

                    {job.error && (
                        <div className="rounded-md border border-destructive/40 bg-destructive/8 p-3 flex items-start gap-2.5">
                            <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <div className="text-[11.5px] font-semibold text-destructive">Hata Detayı</div>
                                <div className="text-[10.5px] text-foreground/85 mt-0.5 font-mono break-all" data-testid={`xml-history-error-${job.job_id}`}>{job.error}</div>
                                {job.retry_count > 0 && <div className="text-[10px] text-muted-foreground mt-1">{job.retry_count} kez otomatik yeniden denendi.</div>}
                            </div>
                        </div>
                    )}

                    {job.finished_at && (
                        <div className="text-[10.5px] text-muted-foreground flex items-center justify-between gap-2 pt-2 border-t border-border/60">
                            <span>Başlangıç: <span className="font-mono">{fmtDate(job.started_at)}</span></span>
                            <span>Bitiş: <span className="font-mono">{fmtDate(job.finished_at)}</span></span>
                        </div>
                    )}

                    {hasRollback && job.status === "success" && (
                        <div className="pt-2 border-t border-border/60 flex items-center justify-between gap-3 flex-wrap">
                            <div className="text-[11px] text-muted-foreground leading-relaxed max-w-md">
                                Bu eşleştirmeden önce &quot;beden tablosu, kalıp, model, atadığınız görsel&quot; değerlerinin
                                otomatik yedeği alındı. Yanlış bir şey olduysa buradan geri alabilirsin.
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onRollback}
                                className="shrink-0"
                                data-testid={`xml-history-rollback-${job.job_id}`}
                            >
                                <RotateCcw className="h-3 w-3 mr-1.5" /> Bu noktaya geri al
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const QuickMetric = ({ icon: Icon, value, tone }) => {
    const cls = tone === "success" ? "text-success" : tone === "info" ? "text-info" : tone === "warning" ? "text-warning" : "text-muted-foreground";
    return (
        <span className={`inline-flex items-center gap-1 text-[11.5px] font-mono tabular-nums ${cls}`}>
            <Icon className="h-3 w-3" strokeWidth={2.25} />{fmtNum(value)}
        </span>
    );
};

const TONE_CLS = {
    default: "text-foreground",
    success: "text-success",
    info:    "text-info",
    warning: "text-warning",
    muted:   "text-muted-foreground",
};
const DetailBox = ({ label, value, icon: Icon, tone = "default", isText = false, testId }) => (
    <div className="rounded-md border border-border bg-muted/20 p-2.5">
        <div className="flex items-center gap-1.5 mb-1">
            <Icon className={`h-3 w-3 ${TONE_CLS[tone]}`} strokeWidth={2.25} />
            <span className="text-[9.5px] font-semibold tracking-[0.1em] uppercase text-muted-foreground">{label}</span>
        </div>
        <div className={`text-[13px] font-bold ${isText ? "" : "font-mono tabular-nums"} leading-tight ${TONE_CLS[tone]}`} data-testid={testId}>
            {isText ? value : fmtNum(value || 0)}
        </div>
    </div>
);
